// Como o celular avisa (0.67, texto 05 D3/G1). Ajustes > Avisos e alarmes define o padrão do app todo;
// cada aba pode ter o seu (Ajustes > Abas > [aba]); as pausas do My Planner, a sessão de estudo e,
// desde o texto 10 (A3), cada alerta de uma tarefa também.
// Todos os avisos respeitam o "Não perturbe" do app (um horário em que o app não toca nem vibra).
//
// Texto 10 (A1): um aviso NUNCA sai mudo por engano.
//  - "Alarme (som)" só aparece quando o app instalado tem os sons próprios (lib/sons.ts). Sem eles, quem
//    escolheu "Alarme (som)" recebe "Som e vibração" com o som padrão do sistema.
//  - Um aviso sem som não vibra em alguns celulares (a vibração do sistema acompanha o som): ali "Vibração"
//    fica escondida e quem já tinha escolhido recebe "Som e vibração".
//  - Os canais do Android são novos (aviso2-...), criados com o som ligado: um canal não muda depois de criado,
//    então os antigos (avisos-...) ficam como estavam e deixam de ser usados.
import { Platform, Vibration } from 'react-native';
import { getDoc } from '../store/store';
import type { AbaId, ModoAviso, TipoVibracao } from '../store/types';
import { N_ } from './i18n';
import { arquivoDoSom, somValido, temSonsProprios } from './sons';

/** Sem vibração própria no aviso (a vibração do sistema só acompanha o som): esconde "Vibração" e os tipos de vibração. */
export const vibracaoPropria = Platform.OS === 'android';

const TODOS_MODOS: [ModoAviso, string][] = [['som', N_('Alarme (som)')], ['vibra', N_('Vibração')], ['ambos', N_('Som e vibração')], ['nada', N_('Desligado')]];
/** Nome de qualquer modo (também dos escondidos), para resumos. */
export const nomeDoModo = (m: ModoAviso) => TODOS_MODOS.find(x => x[0] === m)?.[1] ?? '';
/** O modo pode ser feito neste app instalado? */
export const modoDisponivel = (m: ModoAviso) => (m === 'som' ? temSonsProprios() : m === 'vibra' ? vibracaoPropria : true);
/** Opções de "Como avisar" que funcionam neste app instalado (sem "Alarme (som)" enquanto não houver sons próprios). */
export const MODOS_AVISO: [ModoAviso, string][] = TODOS_MODOS.filter(([m]) => modoDisponivel(m));
/** O modo que vale de verdade: um modo que não pode ser feito aqui vira "Som e vibração" (nunca mudo). */
export const modoReal = (m: ModoAviso): ModoAviso => (modoDisponivel(m) ? m : 'ambos');

/** Modo padrão do app (sem escolha: som e vibração). */
export function avisoDoApp(): ModoAviso {
  try { return modoReal(getDoc('ajustes').avisos?.modo ?? 'ambos'); } catch { return 'ambos'; }
}
/** Modo de uma aba: o escolhido para ela, ou o padrão do app. */
export function modoDaAba(aba: AbaId | null | undefined): ModoAviso {
  try { const m = aba ? getDoc('ajustes').avisosAba?.[aba] : undefined; return m ? modoReal(m) : avisoDoApp(); } catch { return avisoDoApp(); }
}
const minDe = (h: string) => { const [a, b] = h.split(':').map(Number); return (a || 0) * 60 + (b || 0); };
/** A hora cai dentro do "Não perturbe"? (aceita horário que passa da meia-noite, ex.: 22:00 às 07:00) */
export function noNaoPerturbe(d: Date): boolean {
  let np: { on: boolean; de: string; ate: string } | undefined;
  try { np = getDoc('ajustes').avisos?.naoPerturbe; } catch { np = undefined; }
  if (!np?.on) return false;
  const m = d.getHours() * 60 + d.getMinutes(), de = minDe(np.de), ate = minDe(np.ate);
  return de <= ate ? m >= de && m < ate : m >= de || m < ate;
}

/** Som próprio escolhido em Ajustes (só no app que tem os sons); null = som padrão do sistema. */
export function somDoApp(): string | null {
  try { return somValido(getDoc('ajustes').avisos?.som); } catch { return somValido(null); }
}

/** Canais do Android (texto 10, A1). Um canal guarda som e vibração e não muda depois de criado: cada combinação
 *  (modo, tipo de vibração, som) tem o seu id, criado quando for usado (notifications/garantirCanais). */
export const CANAL_SILENCIOSO = 'aviso2-silencioso';
/** Ids antigos (até a 0.71), mantidos para quem ainda os usa ('avisos-silencioso' no "Me avise em..."). */
export const CANAL_DO_MODO: Record<ModoAviso | 'silencioso', string> = {
  ambos: 'aviso2-ambos-dupla', som: 'aviso2-som', vibra: 'aviso2-vibra-dupla', nada: CANAL_SILENCIOSO, silencioso: CANAL_SILENCIOSO,
};
/** Canal do Android para o modo m com o tipo de vibração v e o som próprio s (null = som do sistema). */
export function canalDe(m: ModoAviso, v: TipoVibracao = 'dupla', s: string | null = null): string {
  if (m === 'nada') return CANAL_SILENCIOSO;
  if (m === 'som') return s ? `aviso2-som-${s}` : 'aviso2-som';
  const base = `aviso2-${m}-${v}`;
  return m === 'ambos' && s ? `${base}-${s}` : base;
}
/** Descrição de um canal, para criá-lo (notifications/garantirCanais). */
export type DefCanal = { id: string; modo: ModoAviso | 'silencioso'; vibracao: TipoVibracao; som: string | null };
/** Os canais que os avisos podem usar agora (com o tipo de vibração e o som de Ajustes). */
export function canaisEmUso(v: TipoVibracao = vibracaoDoApp(), s: string | null = somDoApp()): DefCanal[] {
  const l: DefCanal[] = [
    { id: canalDe('ambos', v, null), modo: 'ambos', vibracao: v, som: null },
    { id: canalDe('vibra', v, null), modo: 'vibra', vibracao: v, som: null },
    { id: CANAL_SILENCIOSO, modo: 'silencioso', vibracao: v, som: null },
  ];
  if (s) l.push({ id: canalDe('som', v, s), modo: 'som', vibracao: v, som: s }, { id: canalDe('ambos', v, s), modo: 'ambos', vibracao: v, som: s });
  return l;
}

/** O que usar num aviso marcado para a data d com o modo m: canal do Android, se toca som e qual arquivo.
 *  null = não avisar ("Desligado"). No "Não perturbe": chega sem som nem vibração. */
export function comoAvisar(d: Date, m: ModoAviso): { canal: string; som: boolean; arquivo?: string } | null {
  if (m === 'nada') return null;
  if (noNaoPerturbe(d)) return { canal: CANAL_SILENCIOSO, som: false };
  const real = modoReal(m), v = vibracaoDoApp();
  // som próprio só no "Alarme (som)"; "Som e vibração" usa o som padrão do sistema
  const s = real === 'som' ? somDoApp() : null;
  return { canal: canalDe(real, v, s), som: real !== 'vibra', arquivo: s ? arquivoDoSom(s) : undefined };
}
/** O valor de content.sound de um aviso: o arquivo do som próprio, o som do sistema ou nenhum. */
export const somDoAviso = (c: { som: boolean; arquivo?: string } | null) => (!c || !c.som ? false : c.arquivo ?? 'default');

// ─── Tipos de vibração (texto 07 C1; texto 10 B) ───
export const TIPOS_VIBRACAO: [TipoVibracao, string][] = [['curta', N_('Curta')], ['longa', N_('Longa')], ['dupla', N_('Dupla')], ['crescente', N_('Crescente')]];
/** Desenho de cada tipo: [espera, vibra, pausa, vibra...] em milissegundos. O MESMO no ▶ Sentir e no canal do aviso.
 *  Bem diferentes entre si: um toque rápido, um toque comprido, dois toques e toques que vão aumentando. */
export const PADRAO_VIBRACAO: Record<TipoVibracao, number[]> = {
  curta: [0, 150],
  longa: [0, 1600],
  dupla: [0, 350, 250, 350],
  crescente: [0, 60, 250, 150, 250, 300, 250, 700],
};
/** Tipo de vibração escolhido no app (sem escolha: 'dupla', como era antes). */
export function vibracaoDoApp(): TipoVibracao {
  try { return getDoc('ajustes').avisos?.vibracao ?? 'dupla'; } catch { return 'dupla'; }
}
/** O modo tem vibração? */
export const modoVibra = (m: ModoAviso) => m === 'vibra' || m === 'ambos';

/** ▶ Sentir: vibra agora com o tipo escolhido, com o MESMO desenho do aviso (canal do Android).
 *  Onde o aviso não tem vibração própria, vibra uma vez como a vibração do sistema que acompanha o aviso. */
export function sentirVibracao(v: TipoVibracao) {
  if (Platform.OS === 'web') return;
  try {
    Vibration.cancel();
    if (vibracaoPropria) Vibration.vibrate(PADRAO_VIBRACAO[v]);
    else Vibration.vibrate();
  } catch { /* sem vibração, sem travar */ }
}
/** ▶ Sentir de um modo: a parte da vibração (o som se ouve no ▶ Ouvir da lista de sons).
 *  Devolve false quando o modo não tem nada para sentir (Alarme (som) e Desligado). */
export function sentirModo(m: ModoAviso, v: TipoVibracao = vibracaoDoApp()): boolean {
  if (!modoVibra(modoReal(m))) return false;
  sentirVibracao(v);
  return true;
}
